// Eskom PARCC — build the RtmDraftInput for ONE division × quarter (Phase 2).
//
// The single definition of "which rows, at what level" the Report-to-Management
// auto-draft reads — shared by the composer and the Reports-page draft download so
// they cannot drift. Per-quarter (the RTM route is rtm/:fy/:quarter/:division and
// her document is a quarterly report): the canonical division's rows for that
// quarter, with the EFFECTIVE compliance level applied (override + findings roll-up,
// via the effLevel map the deck already computes). Pure.
import type { Transaction } from '../../../data/types';
import type { FindingRead } from '../../../lib/db';
import { canonicalDivision } from '../../../domain/divisions';
import { normalisePeriod } from '../../../domain/fiscal';
import { withLevels } from '../deckSections';
import { deckDivisionLabel } from '../reportScope';
import type { RtmDraftInput } from './buildRtmDocModel';

export interface BuildRtmDraftArgs {
  fy: string;
  quarter: string;
  division: string;
  reportDate: string;
  classMap?: Record<string, string>;
  classOverrides?: Record<string, string>;
  scopeLimitationRefs?: readonly string[];
  everGapRefs?: ReadonlySet<string>;
  everGapCoverage?: { covered: number; total: number };
}

export function buildRtmDraftInput(
  visible: readonly Transaction[],
  findings: readonly FindingRead[],
  effLevel: ReadonlyMap<string, string>,
  args: BuildRtmDraftArgs,
): RtmDraftInput {
  const division = canonicalDivision(args.division);
  const quarter = normalisePeriod(args.quarter) || args.quarter;
  const divRows = visible.filter(
    (t) =>
      !t.deleted &&
      canonicalDivision(t.division) === division &&
      (normalisePeriod(t.period) || t.period) === quarter,
  );
  const based = withLevels(divRows, (t) => effLevel.get(t.referenceNo) ?? t.complianceLevel ?? '');
  return {
    fy: args.fy,
    quarter: args.quarter,
    division,
    longName: deckDivisionLabel(division),
    reportDate: args.reportDate,
    rows: based,
    findings,
    classMap: args.classMap,
    classOverrides: args.classOverrides,
    scopeLimitationRefs: args.scopeLimitationRefs,
    everGapRefs: args.everGapRefs,
    everGapCoverage: args.everGapCoverage,
  };
}
